import React, { useMemo } from 'react'
import { DialogTitle, DialogContent, DialogActions, MenuItem, Grid } from '@mui/material'
import ResponsiveDialog from 'components/ResponsiveDialog'
import Button from 'components/Button'
import DatePicker from 'components/DatePicker'
import SelectField from 'components/SelectField'
import { useFragment } from 'react-relay'
import { graphql } from 'relay-runtime'
import { AvailabilityEditDialog_User$key } from '__generated__/AvailabilityEditDialog_User.graphql'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { AvailabilityEditDialog_UpdateUserMutation, ProfileAvailabilityTypeLiteral } from '__generated__/AvailabilityEditDialog_UpdateUserMutation.graphql'
import { z } from 'zod'
import FormSubmitButton from 'components/FormSubmitButton'
import Form from 'components/Form'
import AvailabilityOptions from './AvailabilityOptions'

export const formSchema = z.object({
  availability: z.enum(['available_soon', 'available_now', 'not_available']),
  availableAt: z.string().nullable(),
  availabilityType: z.array(z.enum(['full_time', 'part_time'])),
})

type FormFields = z.infer<typeof formSchema>

interface IAvailabilityEditDialogProps {
  user: AvailabilityEditDialog_User$key
  onClose: () => void
}

function AvailabilityEditDialog({ user: userFragmentRef, onClose }: IAvailabilityEditDialogProps) {
  const user = useFragment(graphql`
    fragment AvailabilityEditDialog_User on User {
      profile {
        availability
        availableAt
        availabilityType
      }
    }
  `, userFragmentRef)

  const defaultValues = useMemo(() => ({
    availability: user?.profile?.availability || 'available_now',
    availableAt: user?.profile?.availableAt || null,
    availabilityType: [...(user?.profile?.availabilityType || [])],
  }), [user])

  const form = useForm<FormFields, AvailabilityEditDialog_UpdateUserMutation>({
    enableReinitialize: true,
    defaultValues,
    submitHandlers: useMutationBasedFormSubmitHandlers(
      graphql`
        mutation AvailabilityEditDialog_UpdateUserMutation($input: UpdateUserInput!) {
          updateUser(input: $input) {
            user {
              profile {
                availability
                availableAt
                availabilityType
              }
            }
          }
        }
      `,
      values => ({
        input: {
          profile: {
            availability: values.availability,
            availableAt: values.availableAt,
            availabilityType: values.availabilityType,
          },
        },
      }),
      {
        successMessage: 'Availability updated',
        verifySuccess: result => Boolean(result?.updateUser?.user?.profile?.availability),
        onSuccess: () => {
          onClose()
        },
      },
    ),
  })

  const handleAvailabilityTypeSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    let availabilityType: ProfileAvailabilityTypeLiteral[] = []

    switch (value) {
      case 'both':
        availabilityType = ['part_time', 'full_time']
        break

      case 'part_time':
        availabilityType = ['part_time']
        break

      case 'full_time':
        availabilityType = ['full_time']
        break
    }

    form.setValue('availabilityType', availabilityType)
  }

  let value = form.watch('availableAt')
  let availabilitySelectValue = 'unspecified'

  // TODO: figure out if this should be restored

  // const minValue = moment()

  // if (value && moment(value).isBefore(minValue)) {
  //   value = moment().add(1, 'days').toISOString()
  // }

  const availabilityType = (form.watch('availabilityType') || []) as string[]

  if (availabilityType.includes('part_time') && availabilityType.includes('full_time')) {
    availabilitySelectValue = 'both'
  } else if (availabilityType.includes('part_time')) {
    availabilitySelectValue = 'part_time'
  } else if (availabilityType.includes('full_time')) {
    availabilitySelectValue = 'full_time'
  }

  const availability = form.watch('availability')
  const availabilityField = form.useFormField('availability', 'selection')
  const availableAtField = form.useFormField('availableAt', 'selection')

  return (
    <ResponsiveDialog open maxWidth="sm" onClose={onClose}>
      <Form form={form}>
        <DialogTitle>
          Select your availability
        </DialogTitle>

        <DialogContent style={{ overflow: 'hidden' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AvailabilityOptions
                input={availabilityField.input}
                meta={availabilityField.meta}
              />
            </Grid>

            {availability === 'available_soon' && (
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Available on"
                  fullWidth
                // minDate={minValue}
                  input={{
                    ...availableAtField.input,
                    value,
                  }}
                  meta={availableAtField.meta}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <SelectField
                value={availabilitySelectValue}
                onChange={handleAvailabilityTypeSelectChange}
                fullWidth
                data-cy="profile-availability-type"
                label="Interested in"
              >
                <MenuItem value="full_time" data-cy="availability-type-full_time">
                  Full-time jobs
                </MenuItem>

                <MenuItem value="part_time" data-cy="availability-type-part_time">
                  Part-time jobs
                </MenuItem>

                <MenuItem value="both" data-cy="availability-type-both">
                  Both part-time and full-time jobs
                </MenuItem>
              </SelectField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">Cancel</Button>
          <FormSubmitButton
            color="primary"
            data-cy="profile-availability-close"
          />
        </DialogActions>
      </Form>
    </ResponsiveDialog>
  )
}

export default React.memo(AvailabilityEditDialog)
